// src/interactiveUGC/components/CharacterList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  CircularProgress,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTheme } from "@mui/material/styles";
import { t } from "@lingui/macro";

import {
  getCharacters,
  addCharacter,
  editCharacter,
  deleteCharacter,
} from "../../utils/apis/interactiveStoriesAPI";
import { getManifest, ensureAssetsManifest } from "../../utils/assetsManifest";
import CharacterEditorDialog from "./CharacterEditorDialog";

/* ────────────────────────────── assets helpers ────────────────────────────── */
const imgCache = new Map();
const loadImg = (url) =>
  new Promise((resolve, reject) => {
    if (!url) return resolve(null);
    if (imgCache.has(url)) return resolve(imgCache.get(url));
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgCache.set(url, img);
      resolve(img);
    };
    img.onerror = reject;
    img.src = url;
  });

const asHostingUrl = (p) =>
  !p ? "" : /^https?:\/\//i.test(p) ? p : (p.startsWith("/") ? p : "/" + p);

function useEnsureManifestLoaded() {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await ensureAssetsManifest();
        if (alive) setReady(true);
      } catch (e) {
        if (alive) setError(e?.message || "Failed to load assets manifest.");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  return { ready, error };
}

/* ────────────────────────────── avatar preview ────────────────────────────── */
const TARGET_W = 816;
const TARGET_H = 1456;

/** Transparent-background avatar compositor (clamped small so cards aren't huge) */
function AvatarPreview({ character }) {
  const { ready } = useEnsureManifestLoaded();
  const canvasRef = useRef(null);

  const urls = useMemo(() => {
    if (!ready || !character) return [];
    try {
      const m = getManifest();
      const g = character.gender || "female";

      const urlForPart = (part, idx) => {
        const list = m?.characters?.[g]?.[part] || [];
        const rel = list[idx] || "";
        return asHostingUrl(rel);
      };

      // 🔹 If template > 0, ONLY render the template (premade full character),
      // ignore other parts (top/bottom/eyes/etc.)
      const templateIndex = character.template ?? 0;
      const hasTemplate = templateIndex > 0;

      if (hasTemplate) {
        const templateUrl = urlForPart("template", templateIndex);
        return templateUrl ? [templateUrl] : [];
      }

      // Otherwise, compose from parts as usual
      const hasFullbody =
        character.fullbody !== null && character.fullbody !== undefined;

      return [
        urlForPart("template", templateIndex),
        ...(hasFullbody
          ? [urlForPart("fullbody", character.fullbody ?? 0)]
          : [
              urlForPart("bottom", character.bottom ?? 0),
              urlForPart("top", character.top ?? 0),
            ]),
        urlForPart("eyes", character.eyes ?? 0),
        urlForPart("nose", character.nose ?? 0),
        urlForPart("lips", character.lips ?? 0),
        urlForPart("hair", character.hair ?? 0),
      ];
    } catch {
      return [];
    }
  }, [ready, character]);

  useEffect(() => {
    if (!ready || !canvasRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const imgs = (await Promise.all(urls.map(loadImg))).filter(Boolean);
        if (cancelled) return;
        const c = canvasRef.current;
        const ctx = c.getContext("2d");
        c.width = TARGET_W;
        c.height = TARGET_H;
        ctx.clearRect(0, 0, TARGET_W, TARGET_H); // keep transparent bg
        imgs.forEach((im) => ctx.drawImage(im, 0, 0, TARGET_W, TARGET_H));
      } catch {
        /* ignore draw errors */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, urls]);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: 120, sm: 140, md: 160 },
        aspectRatio: "816 / 1456",
        mx: "auto",
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "transparent",
        minWidth: 0,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ width: "100%", height: "100%", display: "block" }}
      />
    </Box>
  );
}

/* ────────────────────────────── main list ────────────────────────────── */
const CharacterList = ({ storyID }) => {
  const theme = useTheme();
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState(null);

  // ────────────────────────────── Fetch ──────────────────────────────
  const fetchCharacters = async () => {
    setLoading(true);
    try {
      const list = await getCharacters(storyID);
      setCharacters(list || []);
    } catch (err) {
      console.error("Error fetching characters:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (storyID) fetchCharacters();
  }, [storyID]);

  // ────────────────────────────── Dialog ──────────────────────────────
  const handleOpenDialog = (char = null) => {
    setEditingCharacter(char);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCharacter(null);
  };

  // ────────────────────────────── CRUD ──────────────────────────────
  const handleSaveDialog = async (payload) => {
    try {
      if (editingCharacter?.id) {
        await editCharacter(storyID, editingCharacter.id, payload);
      } else {
        await addCharacter(storyID, payload);
      }
      await fetchCharacters();
      handleCloseDialog();
    } catch (err) {
      console.error("Error saving character:", err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCharacter(storyID, id);
      setCharacters((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error("Error deleting character:", err);
    }
  };

  // ────────────────────────────── UI ──────────────────────────────
  return (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : characters.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: "center", my: 3 }}>
          {t`No characters yet.`}
        </Typography>
      ) : (
        <Grid2
          container
          spacing={1.25}
          sx={{
            alignItems: "stretch",
            minWidth: 0,
          }}
        >
          {characters.map((char) => (
            <Grid2
              item
              key={char.id}
              xs={6}
              sm={4}
              md={4}
              sx={{ display: "flex", minWidth: 0 }}
            >
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: theme.shadows[2],
                  width: "100%",
                  display: "grid",
                  gridTemplateRows: "auto 1fr auto",
                  overflow: "hidden",
                  minWidth: 0,
                  p: 0.25,
                }}
              >
                <CardContent sx={{ pb: 0.5, minWidth: 0 }}>
                  <AvatarPreview character={char} />

                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{
                      mt: 0.75,
                      textAlign: "center",
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                      lineHeight: 1.2,
                    }}
                    title={char.name}
                  >
                    {char.name}
                  </Typography>
                </CardContent>

                <Box /> {/* spacer */}

                <CardActions
                  sx={{ justifyContent: "flex-end", px: 0.5, pb: 0.75 }}
                >
                  <IconButton
                    onClick={() => handleOpenDialog(char)}
                    size="small"
                    sx={{ color: "text.primary" }}
                    aria-label={t`Edit ${char.name}`}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete(char.id)}
                    size="small"
                    sx={{ color: "text.primary" }}
                    aria-label={t`Delete ${char.name}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid2>
          ))}
        </Grid2>
      )}

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        fullWidth
        sx={{ mt: 2 }}
        onClick={() => handleOpenDialog()}
      >
        {t`Add New Character`}
      </Button>

      <CharacterEditorDialog
        open={dialogOpen}
        initialValue={editingCharacter || undefined}
        onClose={handleCloseDialog}
        onSave={handleSaveDialog}
      />
    </Box>
  );
};

export default CharacterList;
